import type { Router, RouteLocationNormalized } from "vue-router";
import { getDeviceType } from "@/utils";
import { useUserStore } from "@/store/user";
import { CREATIVE_TEMPLATE_DETAIL_QUERY_KEY } from "@/views/workspace/board/types/creativeTemplate";

/**
 * localStorage key：用户在移动端主动选择「切回桌面版」后置为 "1"。
 * PC 端守卫检测到该标记时跳过移动端重定向，避免循环跳转。
 */
const FORCE_PC_STORAGE_KEY = "force_pc";

/**
 * 已登录用户在守卫内强制刷新 userInfo 的节流间隔
 * 避免每次导航重复拉取用户信息
 */
const USER_INFO_REFRESH_INTERVAL = 30_000;

let lastUserInfoRefreshAt = 0;

/**
 * 团队版用户（当前为团队版视角）应留在 PC 端：移动端暂无团队版能力
 * 未登录用户无法判断团队版，返回 false（维持现状跳移动端）
 */
export function isTeamUserRequiresPc(userStore: { isLogin: boolean; isGroupLogin: boolean }): boolean {
  return userStore.isLogin && userStore.isGroupLogin;
}

/**
 * 移动端域名映射（按环境）
 * - development / test → test-m.holopix.cn
 * - gray → private-m.holopix.cn
 * - production → m.holopix.cn
 */
const MOBILE_DOMAIN_MAP: Readonly<Record<string, string>> = {
  development: "https://test-m.holopix.cn",
  test: "https://test-m.holopix.cn",
  gray: "https://private-m.holopix.cn",
  production: "https://m.holopix.cn"
};

/** 未知环境回退到测试移动端，避免误入正式环境 */
const DEFAULT_MOBILE_DOMAIN = "https://test-m.holopix.cn";

/**
 * 根据当前环境获取移动端域名
 */
export function resolveMobileDomain(environment: string): string {
  return MOBILE_DOMAIN_MAP[environment] ?? DEFAULT_MOBILE_DOMAIN;
}

/**
 * 生成移动端跳转地址，完整保留当前路径、查询参数和 hash
 */
export function buildMobileRedirectUrl(environment: string, fullPath: string): string {
  return `${resolveMobileDomain(environment)}${fullPath}`;
}

function normalizeQueryValue(raw: unknown): string | null {
  if (raw === undefined || raw === null) {
    return null;
  }
  const value = Array.isArray(raw) ? String(raw[0] ?? "") : String(raw);
  return value.trim() ? value : null;
}

function buildQueryString(query: RouteLocationNormalized["query"]): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
    } else {
      searchParams.set(key, String(value));
    }
  }
  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : "";
}

/**
 * 解析 PC 路由对应的移动端路径；无映射时返回 null（不跳转）
 */
export function resolveMobileRedirectPath(to: RouteLocationNormalized): string | null {
  const routeName = to.name as string;

  if (routeName === "workbench" || routeName === "image-detail" || routeName === "model-detail") {
    return to.fullPath;
  }

  if (routeName === "board-all") {
    const templateId = normalizeQueryValue(to.query[CREATIVE_TEMPLATE_DETAIL_QUERY_KEY]);
    if (!templateId || !Number.isFinite(Number(templateId))) {
      return null;
    }

    const nextQuery = { ...to.query };
    delete nextQuery[CREATIVE_TEMPLATE_DETAIL_QUERY_KEY];
    return `/template/${templateId}${buildQueryString(nextQuery)}${to.hash || ""}`;
  }

  return null;
}

/**
 * 判断是否需要重定向到移动端
 *
 * 用户在移动端抽屉点击「切回桌面版网站」后会设置 localStorage["force_pc"] = "1"，
 * 此时跳过重定向，让用户留在 PC 端浏览。
 */
async function shouldRedirect(to: RouteLocationNormalized): Promise<boolean> {
  if (!resolveMobileRedirectPath(to) || getDeviceType() !== "mobile") {
    return false;
  }

  const userStore = useUserStore();
  // 先用共享 Cookie 恢复登录态（跨域/全新设备下 userInfo 可能为空）
  // 恢复失败则留在 PC 端，避免中断导航或循环跳转（fail-safe）
  try {
    const pulledUserInfo = await userStore.synchronizeSharedAuthSession();
    // sync 内部已拉取 userInfo 时记录节流时间戳，避免本次导航重复刷新
    if (pulledUserInfo) {
      lastUserInfoRefreshAt = Date.now();
    }
  } catch {
    return false;
  }

  // 已登录时强制刷新 userInfo，确保 curTeam 新鲜（官网登录可能写入旧值）
  // 刷新失败则留在 PC 端，避免循环跳转（fail-safe）
  if (userStore.isLogin) {
    const now = Date.now();
    if (now - lastUserInfoRefreshAt >= USER_INFO_REFRESH_INTERVAL) {
      try {
        await userStore.queryUserInfo();
        // 刷新成功后才记录时间戳；失败不消耗节流窗口，下次导航可重试
        lastUserInfoRefreshAt = Date.now();
      } catch {
        return false;
      }
    }
  }

  // 团队版用户留在 PC 端（移动端暂无团队版能力）
  if (isTeamUserRequiresPc(userStore)) return false;

  try {
    return localStorage.getItem(FORCE_PC_STORAGE_KEY) !== "1";
  } catch {
    // localStorage 不可用（隐私模式等）时正常重定向
    return true;
  }
}

/**
 * 执行移动端域名跳转
 */
function redirectToMobile(mobilePath: string): void {
  const targetUrl = buildMobileRedirectUrl(import.meta.env.VITE_ENV, mobilePath);
  window.location.href = targetUrl;
}

/**
 * 注册移动端重定向路由守卫
 * 手机端访问指定路由时自动跳转到移动端域名（排除平板设备）
 *
 * @example
 * // 在 routerHandle 中调用：
 * registerMobileRedirectGuard(router);
 */
export function registerMobileRedirectGuard(router: Router): void {
  router.beforeEach(async (to) => {
    const mobilePath = resolveMobileRedirectPath(to);
    if (mobilePath && (await shouldRedirect(to))) {
      redirectToMobile(mobilePath);
      return false;
    }
  });
}
